/**
 * Phase 4f.C — Shop-Warenkorb-Drawer (floating cart panel).
 * Rendered on ShopPage + ShopArticlePage. Shows itemCount badge in the
 * header and slides open as a side-panel with line items, qty selectors,
 * total, and the Stripe-Checkout-Button.
 */
import { useState } from "react";
import { ShoppingCart, X, Loader2, Trash2, ShieldCheck } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useShopCart } from "../lib/shopCart";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const API = process.env.REACT_APP_BACKEND_URL;

export function CartBadgeButton({ slug, onClick }) {
    const { itemCount } = useShopCart(slug);
    return (
        <button
            type="button"
            onClick={onClick}
            className="relative inline-flex items-center gap-1.5 px-3 py-2 rounded-sm bg-[#111827] hover:bg-black text-white font-bold text-xs uppercase tracking-wider"
            data-testid="shop-cart-open-btn"
        >
            <ShoppingCart className="w-4 h-4" />
            Warenkorb
            {itemCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-[#FFCC00] text-[#111827] text-[10px] font-black px-1.5 py-0.5 rounded-full min-w-[18px] text-center" data-testid="shop-cart-badge">
                    {itemCount}
                </span>
            )}
        </button>
    );
}

export function ShopCartDrawer({ slug, open, onClose, dealerInfo }) {
    const { cart, removeItem, setQty, clear, itemCount, total } = useShopCart(slug);
    const [busy, setBusy] = useState(false);
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");

    const startCheckout = async () => {
        if (!cart || !cart.items.length) {
            toast.error("Warenkorb ist leer");
            return;
        }
        if (!email.trim()) {
            toast.error("Bitte E-Mail eingeben für die Bestellbestätigung");
            return;
        }
        setBusy(true);
        try {
            const dealerId = cart.dealer_id || dealerInfo?.id;
            const payload = {
                dealer_id: dealerId,
                items: cart.items.map((it) => ({
                    article_id: it.article_id,
                    quantity: it.quantity,
                })),
                origin_url: window.location.origin,
                buyer_email: email.trim().toLowerCase(),
                buyer_name: name.trim() || undefined,
            };
            const r = await axios.post(`${API}/api/shop/checkout/session`, payload);
            // Stash the session id so the success page can poll even after redirect.
            try {
                sessionStorage.setItem("tw_shop_last_checkout_slug", slug);
                sessionStorage.setItem("tw_shop_last_checkout_session", r.data.session_id);
            } catch { /* sessionStorage full — ignore */ }
            if (r.data?.url) {
                window.location.href = r.data.url;
            } else {
                toast.error("Stripe-Checkout-URL fehlt");
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Checkout fehlgeschlagen");
            setBusy(false);
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex" data-testid="shop-cart-drawer">
            <div className="flex-1 bg-black/80" onClick={onClose} aria-label="schließen" />
            <aside className="w-full max-w-md bg-white shadow-2xl flex flex-col">
                <header className="px-4 py-3 border-b flex items-center justify-between">
                    <h2 className="font-display font-black text-lg flex items-center gap-2">
                        <ShoppingCart className="w-5 h-5" /> Warenkorb
                        {itemCount > 0 && <span className="bg-[#FFCC00] text-[#111827] text-[11px] font-bold px-2 py-0.5 rounded-sm">{itemCount}</span>}
                    </h2>
                    <button onClick={onClose} data-testid="shop-cart-close" aria-label="schließen" className="p-1 hover:bg-gray-100 rounded-sm">
                        <X className="w-5 h-5" />
                    </button>
                </header>

                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {(!cart || !cart.items.length) ? (
                        <div className="text-center py-12 text-gray-500" data-testid="shop-cart-empty">
                            <ShoppingCart className="w-10 h-10 mx-auto text-gray-300 mb-3" />
                            <div>Ihr Warenkorb ist leer.</div>
                            <div className="text-xs mt-2">Klicken Sie bei einem Artikel auf „In den Warenkorb".</div>
                        </div>
                    ) : (
                        cart.items.map((it) => (
                            <div key={it.article_id} className="flex gap-3 border border-gray-200 rounded-sm p-2" data-testid={`shop-cart-row-${it.article_id}`}>
                                <div className="w-16 h-16 shrink-0 bg-gray-50 border border-gray-200 rounded-sm overflow-hidden">
                                    {it.photo_path ? (
                                        <img src={`${API}/api/shop/photo/${it.photo_path}`} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-gray-300">
                                            <ShoppingCart className="w-6 h-6" />
                                        </div>
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-bold text-sm line-clamp-2" data-testid={`shop-cart-name-${it.article_id}`}>{it.name}</div>
                                    <div className="text-[10px] font-mono-num text-gray-500">SKU {it.sku}</div>
                                    <div className="flex items-center justify-between mt-1">
                                        <div className="flex items-center gap-1">
                                            <button
                                                type="button"
                                                onClick={() => setQty(it.article_id, Math.max(1, it.quantity - 1))}
                                                className="w-7 h-7 border border-gray-300 rounded-sm hover:bg-gray-50 font-bold"
                                                aria-label="Menge verringern"
                                                data-testid={`shop-cart-qty-minus-${it.article_id}`}
                                            >–</button>
                                            <input
                                                type="number"
                                                min="1"
                                                max="99"
                                                value={it.quantity}
                                                onChange={(e) => setQty(it.article_id, e.target.value)}
                                                className="w-12 h-7 text-center border border-gray-300 rounded-sm font-mono-num text-sm"
                                                data-testid={`shop-cart-qty-input-${it.article_id}`}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setQty(it.article_id, Math.min(99, it.quantity + 1))}
                                                className="w-7 h-7 border border-gray-300 rounded-sm hover:bg-gray-50 font-bold"
                                                aria-label="Menge erhöhen"
                                                data-testid={`shop-cart-qty-plus-${it.article_id}`}
                                            >+</button>
                                        </div>
                                        <div className="font-mono-num font-bold text-sm" data-testid={`shop-cart-line-total-${it.article_id}`}>
                                            {(it.price_gross * it.quantity).toFixed(2)} €
                                        </div>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => removeItem(it.article_id)}
                                    className="self-start text-gray-400 hover:text-red-600"
                                    aria-label="Entfernen"
                                    data-testid={`shop-cart-remove-${it.article_id}`}
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        ))
                    )}
                </div>

                {cart && cart.items.length > 0 && (
                    <footer className="border-t p-4 space-y-3">
                        <div className="flex items-center justify-between">
                            <span className="text-sm uppercase tracking-wider font-bold text-gray-500">Gesamt (brutto)</span>
                            <span className="font-display font-black text-2xl font-mono-num" data-testid="shop-cart-total">
                                {total.toFixed(2)} €
                            </span>
                        </div>
                        <div className="space-y-2">
                            <Input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="E-Mail für Bestellbestätigung *"
                                className="rounded-sm"
                                data-testid="shop-cart-email"
                            />
                            <Input
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Name (optional)"
                                className="rounded-sm"
                                data-testid="shop-cart-name"
                            />
                        </div>
                        <Button
                            onClick={startCheckout}
                            disabled={busy}
                            className="w-full h-11 rounded-sm bg-[#111827] hover:bg-black text-white font-bold tracking-wider uppercase"
                            data-testid="shop-cart-checkout-btn"
                        >
                            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                            Sicher bezahlen mit Stripe
                        </Button>
                        <div className="text-[10px] text-gray-500 text-center">
                            SSL-verschlüsselt · Kreditkarte / SEPA / Apple-Pay / Google-Pay
                        </div>
                        <button
                            type="button"
                            onClick={() => { if (window.confirm("Warenkorb wirklich leeren?")) clear(); }}
                            className="w-full text-[11px] uppercase tracking-wider text-red-600 hover:underline"
                            data-testid="shop-cart-clear"
                        >
                            Warenkorb leeren
                        </button>
                    </footer>
                )}
            </aside>
        </div>
    );
}
